import React, { useEffect, useState } from 'react';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, Button, Avatar, IconButton, Dialog, DialogTitle, 
  DialogContent, DialogActions, Chip
} from '@mui/material';
import { Visibility, CheckCircle, Cancel } from '@mui/icons-material';
import axios from 'axios';
import './DeliveryPersons.css';

const PendingApprovals = () => {
  const [pendingPersons, setPendingPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchPendingPersons();
  }, []);

  const fetchPendingPersons = async () => {
    try {
      const response = await axios.get('https://api.rismicmart.com/api/delivery-person');
      if (response.data.success) {
        const pending = response.data.data.filter(person => !person.isActive);
        setPendingPersons(pending);
      }
    } catch (error) {
      console.error('Error fetching pending persons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (window.confirm('Approve this delivery person?')) {
      try {
        const response = await axios.put(`https://api.rismicmart.com/api/delivery-person/${id}`, {
          isActive: true
        });
        if (response.data.success) {
          fetchPendingPersons();
          alert('Delivery person approved successfully!');
        }
      } catch (error) {
        console.error('Error approving:', error);
        alert('Failed to approve');
      }
    }
  };

  const handleReject = async (id) => {
    if (window.confirm('Reject this delivery person? This will delete their account.')) {
      try {
        await axios.delete(`https://api.rismicmart.com/api/delivery-person/${id}`);
        fetchPendingPersons();
        alert('Delivery person rejected and deleted!');
      } catch (error) {
        console.error('Error rejecting:', error);
        alert('Failed to reject');
      }
    }
  };

  const handleViewDetails = (person) => {
    setSelectedPerson(person);
    setOpenDialog(true);
  };

  return (
    <div className="delivery-persons-page">
      <div className="page-header">
        <h1>Pending Approvals</h1>
        <div className="header-stats">
          <div className="stat-card">
            <span className="stat-label">Pending</span>
            <span className="stat-value">{pendingPersons.length}</span>
          </div>
        </div>
      </div>

      {pendingPersons.length === 0 && !loading && (
        <div className="empty-state">
          <CheckCircle style={{ fontSize: 64, color: '#4caf50' }} />
          <h2>No Pending Approvals</h2>
          <p>All delivery persons have been reviewed</p>
        </div>
      )}

      {pendingPersons.length > 0 && (
        <TableContainer component={Paper} className="table-container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Profile</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>License</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">Loading...</TableCell>
                </TableRow>
              ) : (
                pendingPersons.map((person) => (
                  <TableRow key={person.id}>
                    <TableCell>
                      <Avatar
                        src={person.profileImage ? `http://localhost:8000${person.profileImage}` : undefined}
                        alt={person.fullName}
                      >
                        {person.fullName.charAt(0)}
                      </Avatar>
                    </TableCell>
                    <TableCell>{person.fullName}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px' }}>{person.phone}</span>
                        <span style={{ fontSize: '12px', color: '#666' }}>{person.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span>{person.vehicleType}</span>
                        {person.vehicleNumber && (
                          <span style={{ fontSize: '12px', color: '#666' }}>{person.vehicleNumber}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{person.licenseNumber || 'N/A'}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleViewDetails(person)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => handleApprove(person.id)}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => handleReject(person.id)}
                        >
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Details Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Review Application</DialogTitle>
        <DialogContent>
          {selectedPerson && (
            <div className="person-details">
              <div className="detail-header">
                <Avatar
                  src={selectedPerson.profileImage ? `http://localhost:8000${selectedPerson.profileImage}` : undefined}
                  alt={selectedPerson.fullName}
                  sx={{ width: 100, height: 100 }}
                >
                  {selectedPerson.fullName.charAt(0)}
                </Avatar>
                <div>
                  <h2>{selectedPerson.fullName}</h2>
                  <Chip label="Pending Approval" color="warning" />
                </div>
              </div>
              <div className="detail-grid">
                <div className="detail-item">
                  <strong>Email:</strong> {selectedPerson.email}
                </div>
                <div className="detail-item">
                  <strong>Phone:</strong> {selectedPerson.phone}
                </div>
                <div className="detail-item">
                  <strong>Vehicle Type:</strong> {selectedPerson.vehicleType}
                </div>
                {selectedPerson.vehicleNumber && (
                  <div className="detail-item">
                    <strong>Vehicle Number:</strong> {selectedPerson.vehicleNumber}
                  </div>
                )}
                {selectedPerson.licenseNumber && (
                  <div className="detail-item">
                    <strong>License Number:</strong> {selectedPerson.licenseNumber}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {selectedPerson && (
            <>
              <Button 
                onClick={() => {
                  handleReject(selectedPerson.id);
                  setOpenDialog(false);
                }}
                color="error"
              >
                Reject
              </Button>
              <Button 
                onClick={() => {
                  handleApprove(selectedPerson.id);
                  setOpenDialog(false);
                }}
                variant="contained"
                color="success"
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PendingApprovals;

